import { Fragment } from 'react'
import { useThemeUI } from 'theme-ui'

import { Link, Type } from '@uswitch/spark'
import { TruArrow, TruCar, TruLocation } from '@uswitch/spark-icons/tru'

import { RichText } from '@uswitch/ev-core/react'
import { ConditionalWrapper } from '@uswitch/ev-utils/conditional-wrapper'
import { useComponentMeta, useDataSelector, useMeta } from '@uswitch/ev-utils/context/selectors'
import { toCamelCase } from '@uswitch/ev-utils/string'

import getComponentVariants from '../../../utils/get-component-variants'
import { Button } from '../../elements/trustyle-button'
import { FormHiddenFields } from '../../helpers/form-hidden-fields'

const POSTCODE_MIN_LENGTH = '5'
const POSTCODE_VALIDATION =
  '\\s*[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\\s*[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}\\s*'

const PrivacyPolicyVariants = [
  'postcode',
  'postcode-secondary',
  'postcode-slimline',
  'broadband-postcode-slimline',
]

const getStyles = element => `modules.lead-capture-form.${element}`

const wrapperStyles = {
  top: [0, 'sm', 'sm'],
  left: 0,
  right: 0,
  '&.sticky-fixed': {
    position: 'fixed !important',
  },
  zIndex: 10,
  display: 'flex',
  justifyContent: 'center',
  pt: [0],
  mt: [0],
  variant: 'modules.lead-capture-form.wrapper',
}

const baseFormStyles = {
  display: 'flex',
  flexDirection: ['column', 'row'],
  alignItems: 'center',
  maxWidth: ['auto', '860px', '860px'],
  mx: 'auto',
  textAlign: 'left',
}

const formStyles = {
  ...baseFormStyles,
  variant: getStyles('form'),
}

const shadowStyles = '0px 2px 20px rgba(0, 0, 0, 0.1)'

const leadStyles = {
  p: [0, 'sm', 'sm'],
  display: 'flex',
  width: '100%',
  backgroundColor: 'white',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'grey-20',
  transition: 'box-shadow 300ms',
  boxShadow: ['none', shadowStyles],
  '.sticky-fixed &': {
    boxShadow: 'unset',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'grey-20',
  },
  variant: getStyles('outer-container'),
}

const fieldsetStyles = {
  display: 'flex',
  flexGrow: 1,
  transition: 'box-shadow 300ms',
  boxShadow: [shadowStyles, 'none'],
  flexDirection: ['column', 'row'],
  variant: getStyles('fieldset'),
}

const inputWrapperStyles = {
  width: '100%',
  position: 'relative',
  '> svg': {
    position: 'absolute',
    top: '50%',
    left: 'sm',
    transform: 'translateY(-50%)',
  },
  '&:not(:last-of-type)': {
    borderWidth: ['0 0 1px 0', '0 1px 0 0'],
    borderStyle: 'solid',
    borderColor: 'grey-20',
  },
}

const postcodeWrapperStyles = {
  ...inputWrapperStyles,
  flexGrow: 0,
  flexShrink: 1,
  svg: {
    display: ['none', 'block'],
    variant: getStyles('postcode-glyph'),
  },
}

const inputStyles = {
  margin: 0,
  py: ['sm', 'sm'],
  pl: ['xs', 'xl'],
  pr: ['xs'],
  border: 'none',
  width: '100%',
  height: '100%',
  variant: getStyles('field-input'),
}

const submitStyles = {
  margin: [0, 'base'],
  fontSize: ['xs', 'sm'],
  variant: getStyles('submit'),
}

const ContainerAndCopy = ({ isSticky, copy, styles, form, privacyPolicyPath }) => (
  <div
    sx={{
      mx: [-15, 0],
      minHeight: isSticky ? ['105px', '172px'] : null,
      ...styles('container'),
    }}
  >
    <ConditionalWrapper
      condition={isSticky}
      wrapper={({ children }) => (
        <div
          sx={{
            ...wrapperStyles,
          }}
          className={isSticky ? 'sticky' : ''}
        >
          {children}
        </div>
      )}
      otherwise={({ children }) => <div>{children}</div>}
    >
      <div sx={styles('innerContainer')}>
        <div sx={{ mx: ['sm', 0], mb: 'md', ...styles('copy') }}>
          {copy && <RichText content={copy} />}
        </div>
        {form}
        <If condition={Boolean(privacyPolicyPath)}>
          {/* for rewrite use privacy policy component in ev-components/components/compounds/privacy-policy */}
          <div sx={{ ...styles('privacy-policy'), mt: 'xxs' }}>
            <Type.Body2>
              <span className='uconsent-text' />{' '}
              <Link
                newTab
                to={privacyPolicyPath}
                text='Privacy Policy'
                sx={{ borderBottom: '1px solid black' }}
              />
            </Type.Body2>
          </div>
        </If>
      </div>
    </ConditionalWrapper>
  </div>
)

const PostcodeInputField = () => {
  const {
    postcodeVariables: { dropUrl },
  } = useComponentMeta('leadCaptureForm')

  return (
    <Fragment>
      <FormHiddenFields action={dropUrl} />
      <input
        sx={inputStyles}
        type='text'
        name='postcode'
        param='postcode'
        placeholder='Postcode'
        required
        pattern={POSTCODE_VALIDATION}
        minLength={POSTCODE_MIN_LENGTH}
      />
      <div sx={{ display: 'none' }}>
        <input
          type='text'
          hidden
          param='targetAction'
          name='targetAction'
          /*
           * FIXME: This clickout URL should be redirected on the same domain like the click-out-form.jsx
           * These redirects should be managed through the content model or meta
           */
          defaultValue={dropUrl}
        ></input>
      </div>
    </Fragment>
  )
}

const PostcodeBroadbandInputField = ({ returnUrl }) => (
  <Fragment>
    <If condition={returnUrl}>
      <input type='hidden' name='returnUrl' value={returnUrl} />
    </If>
    <input
      sx={inputStyles}
      type='text'
      name='postcode'
      param='postcode'
      placeholder='Postcode'
      required
      pattern={POSTCODE_VALIDATION}
      minLength={POSTCODE_MIN_LENGTH}
    />
  </Fragment>
)

const PostcodeHomeInsuranceInputField = () => {
  const {
    postcodeHomeInsuranceVariables: { category, supplier, productName, pid, linkId, ln, page },
  } = useComponentMeta('leadCaptureForm')

  return (
    <Fragment>
      <input
        sx={inputStyles}
        type='text'
        name='postCode'
        placeholder='Postcode'
        required
        pattern={POSTCODE_VALIDATION}
        minLength={POSTCODE_MIN_LENGTH}
      />
      <div sx={{ display: 'none' }}>
        <input type='hidden' name='category' value={category} />
        <input type='hidden' name='supplier' value={supplier} />
        <input type='hidden' name='product_name' value={productName} />
        <input type='hidden' name='pid' value={pid} />
        <input type='hidden' name='link_id' value={linkId} />
        <input type='hidden' name='ln' value={ln} />
        <input type='hidden' name='page' value={page} />
      </div>
    </Fragment>
  )
}

const PostcodeHomeInsuranceCstackInputField = () => {
  const {
    postcodeHomeInsuranceVariables: { category, supplier, productName, pid, linkId, ln, page },
  } = useComponentMeta('leadCaptureForm')

  return (
    <Fragment>
      <input
        sx={inputStyles}
        type='text'
        name='postCode'
        placeholder='Postcode'
        required
        pattern={POSTCODE_VALIDATION}
        minLength={POSTCODE_MIN_LENGTH}
      />
      <div sx={{ display: 'none' }}>
        <input type='hidden' name='category' value={category} />
        <input type='hidden' name='supplier' value={supplier} />
        <input type='hidden' name='product_name' value={productName} />
        <input type='hidden' name='pid' value={pid} />
        <input type='hidden' name='link_id' value={linkId} />
        <input type='hidden' name='ln' value={ln} />
        <input type='hidden' name='page' value={page} />
      </div>
    </Fragment>
  )
}

const CarRegistrationInputField = () => {
  const {
    carInsuranceVariables: { category, supplier, productName, pid, ln },
  } = useComponentMeta('leadCaptureForm')

  const pathName = useDataSelector(['location', 'pathname'], '')

  return (
    <Fragment>
      <input
        sx={inputStyles}
        type='text'
        name='regNumber'
        placeholder='Registration No. (optional)'
      />
      <div sx={{ display: 'none' }}>
        <input type='hidden' name='category' value={category} />
        <input type='hidden' name='supplier' value={supplier} />
        <input type='hidden' name='product_name' value={productName} />
        <input type='hidden' name='pid' value={pid} />
        <input type='hidden' name='ln' value={ln} />
        <input type='hidden' name='page' value={pathName} />
      </div>
    </Fragment>
  )
}

const InputField = ({ variant, returnUrl }) => {
  if (variant?.match('car-insurance')) {
    return <CarRegistrationInputField />
  }
  if (variant?.match('home-insurance-postcode-cstack')) {
    return <PostcodeHomeInsuranceCstackInputField />
  }
  if (variant?.match('home-insurance-postcode')) {
    return <PostcodeHomeInsuranceInputField />
  }

  if (variant?.match('broadband')) {
    return <PostcodeBroadbandInputField returnUrl={returnUrl} />
  }

  return <PostcodeInputField />
}

const InputIcon = ({ variant }) => {
  const { theme } = useThemeUI()
  const isCarInsurance = variant?.match('car-insurance')
  const size = isCarInsurance ? theme.fontSizes.sm : theme.fontSizes.md

  return isCarInsurance ? (
    <TruCar
      color={theme.colors['grey-50']}
      size={size}
      sx={{ display: ['none', 'block'], variant: getStyles('glyph') }}
    />
  ) : (
    <TruLocation
      color={theme.colors['grey-50']}
      size={size}
      sx={{ display: ['none', 'block'], variant: getStyles('glyph') }}
    />
  )
}

export const RegistrationPostcodeForm = ({
  submitButtonText = 'Compare',
  copy,
  isSticky = false,
  action,
  returnUrl,
  variant,
}) => {
  const { theme } = useThemeUI()
  const layout = toCamelCase(variant)
  const styles = getComponentVariants(theme, 'modules', 'lead-capture-form', [layout])
  const privacyPolicyPath = useMeta('privacyPolicyPath')

  return (
    <ContainerAndCopy
      isSticky={isSticky}
      styles={styles}
      copy={copy}
      privacyPolicyPath={PrivacyPolicyVariants.includes(variant) && privacyPolicyPath}
      form={
        <form sx={{ ...formStyles, ...styles('form') }} action={action} method='GET'>
          <div
            sx={{ ...leadStyles, ...styles('outer-container') }}
            data-component-name='lead-capture-form'
          >
            <div sx={fieldsetStyles}>
              <div sx={postcodeWrapperStyles}>
                <InputIcon variant={variant} />
              </div>
            </div>

            <FormHiddenFields action={action} />
            <InputField variant={variant} returnUrl={returnUrl} />

            <Button
              sx={submitStyles}
              variant='primary'
              iconPosition='right'
              onClick={e =>
                e.target.dispatchEvent(
                  new CustomEvent('leadCaptureFormSubmitted', { bubbles: true, detail: {} }),
                )
              }
              type='submit'
            >
              {submitButtonText}
              <TruArrow
                sx={{ transform: 'rotate(0.25turn)' }}
                color={theme.colors['light-1']}
                size={theme.fontSizes.xs}
              />
            </Button>
          </div>
        </form>
      }
    />
  )
}
export default RegistrationPostcodeForm
